import { Avatar } from 'src/design/atoms/Avatar';
import { Group } from 'src/design/atoms/Group';
import { Text } from 'src/design/atoms/Text';
import { Input } from 'src/design/atoms/Input';
import React from 'react';
import { Mention, MentionsInput } from 'react-mentions';
import { useOrganization } from 'src/hooks/useOrganization';

import { OrganizationRoleDto, UserDto } from 'src/api/FiretrolApiClient';
import classNames from './mention-input.module.css';

interface MentionInputProps {
  peopleOptions: UserDto[];
  id?: string;
  value: string;
  disabled?: boolean;
  label?: string;
  onChange: (
    value: string,
    plainTextValue: string,
    people: UserDto[],
    roles: OrganizationRoleDto[],
  ) => void;
}

const dedupUsers = (users: UserDto[]) => {
  const seen = new Set();
  return users.filter((user) => {
    const duplicate = seen.has(user.id);
    seen.add(user.id);
    return !duplicate;
  });
};

export const MentionInput: React.FC<MentionInputProps> = ({
  peopleOptions,
  value,
  onChange,
  disabled = false,
  id = 'mention-input-1',
  label = 'Content',
}) => {
  const { roles } = useOrganization();

  React.useLayoutEffect(() => {
    const input: any = document.getElementById(id);
    if (input) {
      input.placeholder = 'What would you like to say?';
    }
  }, [id]);

  const roleOptions = React.useMemo(() => {
    return roles.map((role) => {
      return {
        ...role,
        id: `role-${role.id}`,
        display: role.name,
      };
    });
  }, [roles]);

  const userOptions = React.useMemo(() => {
    return peopleOptions.map((user) => ({
      ...user,
      display: user.fullName,
    }));
  }, [peopleOptions]);

  const renderSuggestion = (d: any) => {
    return (
      <Group gap="xs">
        {d.scopes ? <></> : <Avatar size="sm" radius="xl" />}
        <Text c="black" size="sm">
          {d.display}
        </Text>
      </Group>
    );
  };

  const onMentionChange = (
    e: any,
    newValue: string,
    newPlainTextValue: string,
    mentions: any[],
  ) => {
    const updatedMentions = dedupUsers(mentions)
      .map((m) => {
        const t = peopleOptions.find((user) => user.id === Number(m.id));
        return t || null;
      })
      .filter((n) => n !== null);

    const rolesOnly = mentions
      .filter((r) => r && r.id.includes('role'))
      .map((t) => {
        if (!t) return null;
        return {
          ...t,
          id: Number(t.id.replace('role-', '')),
        };
      });

    const updatedRoles = dedupUsers(rolesOnly)
      .map((m) => {
        const t = roles.find((r) => r.id === m.id);
        return t || null;
      })
      .filter((n) => n !== null);

    onChange(newValue, newPlainTextValue, updatedMentions, updatedRoles);
  };

  // @FIX: Highlight of mentions is not working despite CSS classes being applied

  return (
    <Input.Wrapper label={label}>
      <MentionsInput
        value={value}
        onChange={onMentionChange}
        className="mentions"
        classNames={classNames}
        id={id}
        allowSuggestionsAboveCursor
        disabled={disabled}
      >
        <Mention
          trigger="@"
          appendSpaceOnAdd
          displayTransform={(id: number, display: string) => `@${display}`}
          data={[...roleOptions, ...userOptions]}
          renderSuggestion={renderSuggestion}
          className="mentions__mention"
        />
      </MentionsInput>
    </Input.Wrapper>
  );
};
